<template>
    <div class="publications">
        <b-container fluid>
            <div
                v-sticky="{zIndex: 12, stickyTop: 55}"
                class="bg-white py-3"
            >
                <div class="d-md-flex align-items-center m-0">
                    <!-- page title -->
                    <div class="h3 mb-3 mr-3">
                        Publications
                    </div>
                </div>

                <div class="d-md-flex align-items-center">
                    <!-- page search -->
                    <div class="flex-grow-1 mr-1 mb-md-0 mb-3">
                        <b-form-input type="search" v-model="search_term" placeholder="Search"/>
                    </div>

                    <div class="d-flex">
                        <div class="mr-1">
                            <b-btn
                                variant="light"
                                :class="filters_visible ? 'active' : 'collapsed'"
                                @click="filters_visible = !filters_visible"
                            >
                                <font-awesome-icon :icon="['fad', 'filter']"/>
                            </b-btn>
                        </div>

                        <!-- page actions -->
                        <div class="ml-auto">
                            <b-button
                                variant="success"
                                @click.prevent="createPublication"
                                :disabled="!can_create_publication"
                            >
                                <font-awesome-icon :icon="['fad', 'plus-square']"/>
                                Create Publication
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="loading_publications">
                <b-spinner label="Loading..."></b-spinner>
            </div>

            <div v-else>
                <b-collapse
                    id="filters"
                    v-model="filters_visible"
                >
                    <div v-if="filters_visible" class="mb-3">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <div class="row align-items-center">
                                    <div class="col-lg">
                                        <multiselect
                                            :select-label="select_label"
                                            :deselect-label="deselect_label"
                                            v-model="filters.categories"
                                            :options="categories"
                                            label="name"
                                            track-by="category"
                                            placeholder="Category"
                                            multiple
                                        />
                                    </div>
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <multiselect
                                            :select-label="select_label"
                                            :deselect-label="deselect_label"
                                            v-model="filters.states"
                                            :options="available_states"
                                            label="name"
                                            track-by="name"
                                            placeholder="States"
                                            multiple
                                        />
                                    </div>
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <multiselect
                                            :select-label="select_label"
                                            :deselect-label="deselect_label"
                                            v-model="filters.grades"
                                            :options="grades"
                                            multiple
                                            label="grade"
                                            track-by="grade"
                                            placeholder="Grades"
                                        >
                                            <template #option="{ option }">
                                                <span v-if="option.grade == 'p' || option.grade == 0">
                                                    {{ option.name }}
                                                </span>
                                                <span v-else>
                                                    {{ option.grade }}
                                                </span>
                                            </template>
                                        </multiselect>
                                    </div>
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <multiselect
                                            :select-label="select_label"
                                            :deselect-label="deselect_label"
                                            v-model="filters.subjects"
                                            :options="subjects"
                                            multiple
                                            label="name"
                                            track-by="subject"
                                            placeholder="Subjects"
                                        >
                                        </multiselect>
                                    </div>
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <multiselect
                                            :select-label="select_label"
                                            :deselect-label="deselect_label"
                                            v-model="filters.school_years"
                                            :options="available_school_years"
                                            multiple
                                            label="school_year"
                                            track-by="school_year"
                                            placeholder="School Years"
                                        />
                                    </div>
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <language-multiselect
                                            :select_label="select_label"
                                            :deselect_label="deselect_label"
                                            v-model="filters.languages"
                                            :options="available_languages"
                                            @add="addLanguage"
                                            @remove="removeLanguage"
                                        />
                                    </div>
                                    <div class="col-lg mt-lg-0 mt-1">
                                        <multiselect
                                            :select-label="select_label"
                                            :deselect-label="deselect_label"
                                            v-model="filters.phases"
                                            :options="available_phases"
                                            multiple
                                            label="phase"
                                            track-by="phase"
                                            placeholder="Phases"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div v-if="active_filters" class="ml-3">
                                <b-btn variant="danger" @click="clearFilters">
                                    <font-awesome-icon :icon="['fad', 'backspace']"/>
                                    Clear All Filters
                                </b-btn>
                            </div>
                        </div>
                    </div>
                </b-collapse>

                <b-table
                    class="publications-table"
                    striped
                    responsive
                    sticky-header
                    outlined
                    no-border-collapse
                    head-variant="light"
                    :busy="loading_publications"
                    :items="ordered_filtered_publications"
                    :fields="fields"
                    :sort-by.sync="sort_by"
                    :sort-desc.sync="sort_desc"
                >
                    <template #table-busy>
                        <b-spinner class="align-middle"></b-spinner>
                    </template>

                    <template #head(created)>
                        Created
                    </template>

                    <template #head(published_at)>
                        Last Published
                    </template>

                    <template #head(locked)>
                        Locked
                    </template>

                    <template #cell(title)="data">
                        <div v-if="data.item && data.item.title" class="publication-title">
                            <router-link :to="{name: 'Publication', params: {publication_id: data.item.id}}">
                                <strong>
                                    {{ data.item.title }}
                                    <span v-if="data.item.category == 'retired'"
                                        class="text-danger">(Retired)
                                    </span>
                                </strong>
                            </router-link>
                            <span
                                v-if="data.item.internal_notes"
                                v-b-tooltip.hover.html
                                :title="data.item.internal_notes"
                                class="text-primary"
                            >
                                <font-awesome-icon :icon="['fad', 'exclamation-circle']" fixed-width />
                            </span>
                        </div>
                        <div v-else>
                            <span class="text-muted">
                                no title
                            </span>
                        </div>
                    </template>

                    <template #cell(state)="data">
                        <div v-if="data.item.state_grades.length > 1">
                            <div class="h6 mb-3 text-center">
                                <span
                                    class="collapse-icon mr-1"
                                    :class="stateGradeIsHidden(data.item.id) ? null : 'collapsed'"
                                    :aria-expanded="stateGradeIsHidden(data.item.id) ? null : 'false'"
                                    @click="toggleStateGrade(data.item.id)"
                                >
                                    <span class="open">
                                        <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width/>
                                    </span>
                                    <span class="closed">
                                        <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width/>
                                    </span>
                                    Mixed
                                </span>
                            </div>
                        </div>

                        <div v-if="data.item.state_grades.length > 1">
                            <div v-if="stateGradeIsHidden(data.item.id)">
                                <div v-for="(row, index) in data.item.state_grades" :key="index" class="text-center">
                                    {{ row.state }}
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <div v-for="(row, index) in data.item.state_grades" :key="index" class="text-center">
                                {{ row.state }}
                            </div>
                        </div>
                    </template>

                    <template #cell(grade)="data">
                        <div v-if="data.item.state_grades.length > 1">
                            <div class="h6 mb-3 text-center">
                                <span
                                    class="collapse-icon mr-1"
                                    :class="stateGradeIsHidden(data.item.id) ? null : 'collapsed'"
                                    :aria-expanded="stateGradeIsHidden(data.item.id) ? null : 'false'"
                                    @click="toggleStateGrade(data.item.id)"
                                >
                                    <span class="open">
                                        <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width/>
                                    </span>
                                    <span class="closed">
                                        <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width/>
                                    </span>
                                    Mixed
                                </span>
                            </div>
                        </div>

                        <div v-if="data.item.state_grades.length > 1">
                            <div v-if="stateGradeIsHidden(data.item.id)">
                                <div v-for="(row, index) in data.item.state_grades" :key="index" class="text-center">
                                    <span v-if="row.grade == '-1'">Early Learning</span>
                                    <span v-else-if="row.grade == 0">Kindergarten</span>
                                    <span v-else-if="row.grade == '-3'">Other</span>
                                    <span v-else>
                                        {{ row.grade }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <div v-for="(row, index) in data.item.state_grades" :key="index" class="text-center">
                                <span v-if="row.grade == '-1'">Early Learning</span>
                                <span v-else-if="row.grade == 0">Kindergarten</span>
                                <span v-else-if="row.grade == '-3'">Other</span>
                                <span v-else>
                                    {{ row.grade }}
                                </span>
                            </div>
                        </div>
                    </template>

                    <template #cell(language)="data">
                        <div class="d-flex align-items-center text-uppercase">
                            <div class="mr-1">
                                <span v-if="data.item.language == 'en' || data.item.language == 'us'">
                                    <flag iso="us"/>
                                </span>
                                <span v-if="data.item.language == 'es' || data.item.language == 'sp'">
                                    <flag iso="es"/>
                                </span>
                            </div>
                            <div>
                                {{ data.item.language }}
                            </div>
                        </div>
                    </template>

                    <template #cell(phase)="data">
                        <div v-if="data.item.publish3_status" class="mt-1">
                            <b-badge
                                @click="getStatusMessage(data.item)"
                                class="text-capitalize"
                                :class="{
                                    'badge-success': data.item.publish3_status == 'imported',
                                    'badge-warning text-white': data.item.publish3_status == 'queued' || data.item.publish3_status == 'processing',
                                    'badge-danger': data.item.publish3_status == 'failed',
                                    'badge-secondary': data.item.publish3_status == 'unpublished'
                                }"
                            >
                                <template v-if="data.item.publish3_status == 'unpublished'">
                                    Not Imported
                                </template>
                                <template v-else>
                                    {{ formattedPhase(data.item.publish3_status) }}
                                </template>
                            </b-badge>
                        </div>
                    </template>

                    <template #cell(category)="data">
                        <div class="d-flex align-items-center justify-content-center">
                            {{ formattedCategory(data.item.category) }}
                        </div>
                    </template>

                    <template #cell(published_at)="data">
                        <span v-if="data.item.published3_at">
                            {{ moment(data.item.published3_at).format("MM-DD-YYYY") }}
                        </span>
                    </template>

                    <template #cell(locked)="data">
                        {{ data.item.locked ? 'Locked' : 'Unlocked' }}
                    </template>

                    <template #cell(actions)="data">
                        <div class="d-flex align-items-center flex-nowrap">
                            <div class="mx-auto">
                                <b-dropdown
                                    variant="secondary"
                                    dropleft
                                    right
                                    boundary="viewport"
                                    :popper-opts="{ positionFixed: true }"
                                >
                                    <template #button-content>
                                        <font-awesome-icon :icon="['fad', 'cog']"/>
                                    </template>
                                    <b-dropdown-item
                                        v-if="data.item.container_id"
                                        :href="(host == 'sbp.studiesweekly.com' ? '//app.' : '//stage.') + 'studiesweekly.com/online/publications/' + data.item.container_id"
                                        target="_blank"
                                    >
                                        <font-awesome-icon :icon="['fad', 'apple-alt']" class="text-danger"
                                                           fixed-width/>
                                        View on SWO
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click.prevent="editPublication(data.item)"
                                    >
                                        <font-awesome-icon :icon="['fad', 'edit']" class="text-primary" fixed-width/>
                                        Edit
                                    </b-dropdown-item>

                                    <!-- added back into trunk 4/16/2024 -->
                                    <b-dropdown-item
                                        @click.prevent="startPublicationDuplication(data.item)"
                                        :disabled="!can_duplicate_publication"
                                    >
                                        <font-awesome-icon :icon="['fad', 'clone']" class="text-info" fixed-width/>
                                        Duplicate
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click="publishPublication3FromPublicationList(data.item)"
                                        :disabled="!can_import_publication || data.item.locked"
                                    >
                                        <font-awesome-icon :icon="['fad', 'share']" class="text-success" fixed-width/>
                                        Import
                                        <div v-if="data.item.publish3_status == 'unpublished'"
                                             style="padding-left: 23px"><small>Status: Not Imported</small></div>
                                        <div v-else style="padding-left: 23px"><small>Status:
                                            {{ data.item.publish3_status }}</small></div>
                                    </b-dropdown-item>
                                    <b-dropdown-item @click.prevent="importHistory(data.item)">
                                        <font-awesome-icon :icon="['fad', 'history']" class="text-secondary" fixed-width/>
                                        Import History
                                    </b-dropdown-item>
                                    <b-dropdown-item @click.prevent="contentReport(data.item)">
                                        <font-awesome-icon
                                            :icon="['fad', 'list-ol']"
                                            class="text-info"
                                            fixed-width
                                        />
                                        Content Report
                                    </b-dropdown-item>
                                    <b-dropdown-item @click.prevent="exportTranslationJSON(data.item)">
                                        <font-awesome-icon
                                            :icon="['fad', 'hockey-mask']"
                                            class="text-secondary"
                                            fixed-width
                                        />
                                        Export JSON
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click.prevent="importTranslationJSON(data.item)"
                                        :disabled="!can_import_json || data.item.locked"
                                    >
                                        <font-awesome-icon
                                            :icon="['fad', 'hockey-mask']"
                                            class="text-danger"
                                            fixed-width
                                        />
                                        Import JSON
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click.prevent="toggleLockPublication(data.item)"
                                        :disabled="!can_lock_publication"
                                    >
                                        <font-awesome-icon
                                            :icon="['fad', data.item.locked ? 'unlock' : 'lock']"
                                            class="text-danger"
                                            fixed-width
                                        />
                                        {{ data.item.locked ? 'Unlock' : 'Lock' }}
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click.prevent="deletePublication(data.item, data.index)"
                                        :disabled="!can_delete_publication || data.item.locked"
                                    >
                                        <font-awesome-icon :icon="['fad', 'trash']" class="text-danger" fixed-width/>
                                        Delete
                                    </b-dropdown-item>
                                </b-dropdown>
                            </div>
                        </div>
                    </template>
                </b-table>
            </div>
        </b-container>

        <publication-modal
            v-model="show_publication_modal"
            :publication="publication"
            :states="states"
            :grades="grades"
            @create="publicationCreated"
            @update="getPublications"
            @hide="show_publication_modal = false"
        />

        <duplicate-publication-modal
            v-model="show_duplicate_publication_modal"
            :publication="publication"
            @duplicated="publicationDuplicated"
            @hide="show_duplicate_publication_modal = false"
        />

        <content-report-modal
            v-model="show_content_report_modal"
            :publication="publication"
            @hide="show_content_report_modal = false"
        />

        <import-history-modal
            v-model="show_import_history_modal"
            :publication="publication"
            @hide="show_import_history_modal = false"
        />

        <import-translation-json-modal
            v-model="show_import_translation_json_modal"
            :publication="publication"
            @uploaded="show_import_translation_json_modal = false"
            @hide="show_import_translation_json_modal = false"
        />

        <export-translation-json-modal
            v-model="show_export_translation_json_modal"
            :publication="publication"
            @uploaded="show_export_translation_json_modal = false"
            @hide="show_export_translation_json_modal = false"
        />

        <panel-validation-failed-modal
            v-model="show_panel_validation_failed_modal"
            :untitled_modals="untitled_modals"
            @hide="show_panel_validation_failed_modal = false"
        />


    </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/_colors.scss";

.publications-table:deep(td) {
    border-left: 1px solid #999;
    border-right: 1px solid #999;
    border-bottom: 1px solid #999;
    padding: 1em;
}
</style>

<script>
import Vue from 'vue'

import moment from 'moment'

Vue.prototype.moment = moment

import VueSticky from 'vue-sticky'

import states from '@/common/states'
import grades from '@/common/grades'
import subjects from '@/common/subjects'
import categories from '@/common/publication_categories'

import LanguageMultiselect from '@/components/LanguageMultiselect'

import PublicationModal from '@/components/modals/publication/PublicationModal'
import DuplicatePublicationModal from '@/components/modals/publication/DuplicatePublicationModal'
import ImportTranslationJsonModal from '@/components/modals/publication/ImportTranslationJsonModal'
import ExportTranslationJsonModal from '@/components/modals/publication/ExportTranslationJsonModal'
import ContentReportModal from '@/components/modals/publication/ContentReportModal'
import ImportHistoryModal from '@/components/modals/publication/ImportHistoryModal'
import PanelValidationFailedModal from '@/components/modals/publication/PanelValidationFailedModal'
import {mapState} from 'vuex'

export default {
    name: 'Publications',
    data: () => ({
        states: states,
        grades: grades,
        subjects: subjects,
        categories: categories,

        publications: [],
        legacy_id_options: [],

        filters: {states: [], grades: [], school_years: [], languages: [], phases: [], subjects: [], categories: []},

        publication: {},

        search_term: '',

        sort_by: 'sku',
        sort_desc: null,

        filters_visible: false,

        loading_publications: false,

        show_publication_modal: false,
        show_duplicate_publication_modal: false,
        show_import_translation_json_modal: false,
        show_export_translation_json_modal: false,
        show_panel_validation_failed_modal: false,
        show_content_report_modal: false,
        show_import_history_modal: false,

        host: '',

        show_state_grades: [],
        untitled_modals: [],
        // show_retired: false,
        select_label: '',
        deselect_label: 'remove'
    }),
    components: {
        LanguageMultiselect,

        PublicationModal,
        DuplicatePublicationModal,
        ImportTranslationJsonModal,
        ExportTranslationJsonModal,
        ContentReportModal,
        ImportHistoryModal,
        PanelValidationFailedModal
    },
    directives: {
        'sticky': VueSticky,
    },
    computed: {
        ...mapState({
            errors: state => state.errors.errors,
            user: state => state.user.user
        }),
        fields() {
            let fields = [
                {
                    key: 'id',
                    class: 'text-uppercase',
                    sortable: true,
                    order_number: 1
                },
                {
                    key: 'title',
                    sortable: true,
                    order_number: 2
                },
                {
                    key: 'portal_sku',
                    label: 'Portal SKU',
                    sortable: true,
                    order_number: 3,
                    thClass: 'text-center',
                    tdClass: 'text-center',
                },
                {
                    key: 'sku',
                    class: 'text-uppercase text-center',
                    sortable: true,
                    order_number: 4
                },
                {
                    key: 'state',
                    class: 'text-center',
                    sortable: true,
                    order_number: 5
                },
                {
                    key: 'grade',
                    sortable: true,
                    class: 'text-center',
                    order_number: 6
                },
                {
                    key: 'school_year',
                    sortable: true,
                    order_number: 7
                },
                {
                    key: 'language',
                    sortable: true,
                    order_number: 8
                },
                {
                    key: 'created',
                    class: 'text-center',
                    sortable: true,
                    order_number: 9
                },
                {
                    key: 'phase',
                    sortable: true,
                    order_number: 10
                },
                {
                    key: 'category',
                    class: 'text-center',
                    sortable: true,
                    order_number: 11
                },
                {
                    key: 'published_at',
                    class: 'text-center',
                    sortable: true,
                    order_number: 12
                }, {
                    key: 'locked',
                    class: 'text-center',
                    sortable: true,
                    order_number: 13
                },
                {
                    key: 'actions',
                    sortable: false,
                    order_number: 14
                },
            ]

            return _.orderBy(fields, 'order_number')
        },
        filtered_publications() {
            return this.publications.filter(publication => {
                let search = this.search_term.toLowerCase()
                let is_branch = publication.is_trunk === 'false';
                let search_match = false
                
                const set_lower_or_empty = field_value => (field_value == null ? '' : String(field_value).toLowerCase());
                let pub_id = set_lower_or_empty(publication.id).includes(search);
                let title = set_lower_or_empty(publication.title).includes(search);
                let sku = set_lower_or_empty(publication.sku).includes(search);
                let school_year = set_lower_or_empty(publication.school_year).includes(search);
                let portal_sku = set_lower_or_empty(publication.portal_sku).includes(search);

                if(
                    search == '' ||
                    title ||
                    sku ||
                    school_year ||
                    portal_sku ||
                    pub_id
                ){
                    search_match = true;
                }

                //when pubs imported from SWO came in there were some without the state_grades set
                if (!publication.state_grades) {
                    publication.state_grades = [{
                        state: 'Not Provided',
                        grade: 'Not Provided',
                    }];
                }

                // Categories filter
                let categories_match = false
                if (_.find(this.filters.categories, {category: publication.category})) {
                    categories_match = true
                }
                categories_match = categories_match || !this.filters.categories.length

                // States filter
                let matching_states = []
                let states_match = false
                matching_states = publication.state_grades.filter(sg => {
                    if (_.find(this.filters.states, {name: sg.state})) {
                        return sg
                    }
                })
                states_match = matching_states.length || !this.filters.states.length

                // Grades filter
                let matching_grades = []
                let grades_match = false
                matching_grades = publication.state_grades.filter(sg => {
                    if (_.find(this.filters.grades, {grade: sg.grade})) {
                        return sg
                    }
                })
                grades_match = matching_grades.length || !this.filters.grades.length

                // subjects filter
                let subjects_match = false
                if (_.find(this.filters.subjects, {subject: publication.subject})) {
                    subjects_match = true
                }
                subjects_match = subjects_match || !this.filters.subjects.length

                // School Years filter
                let school_years_match = false
                if (_.find(this.filters.school_years, {school_year: publication.school_year})) {
                    school_years_match = true
                }
                school_years_match = school_years_match || !this.filters.school_years.length

                // Languages filter
                let languages_match = false
                if (_.find(this.filters.languages, {language: publication.language})) {
                    languages_match = true
                }
                languages_match = languages_match || !this.filters.languages.length

                // Phases filter
                let phases_match = false
                if (_.find(this.filters.phases, {phase: publication.phase})) {
                    phases_match = true
                }
                phases_match = phases_match || !this.filters.phases.length

                return search_match && categories_match && states_match && grades_match && subjects_match && school_years_match && languages_match && phases_match && is_branch
            })
        },
        ordered_filtered_publications() {
            return _.orderBy(this.filtered_publications, 'created')
        },
        available_states() {
            let available_states = []

            _.forEach(this.publications, publication => {
                _.forEach(publication.state_grades, row => {
                    available_states.push({
                        name: row.state
                    })
                })
            })

            available_states = _.orderBy(available_states, 'name')
            available_states = _.uniqBy(available_states, 'name')

            return available_states
        },
        available_school_years() {
            let school_years = []

            _.forEach(this.publications, publication => {
                school_years.push({
                    school_year: publication.school_year
                })
            })

            school_years = _.orderBy(school_years, 'school_year', 'desc')
            school_years = _.uniqBy(school_years, 'school_year')

            return school_years
        },
        available_languages() {
            let languages = []

            _.forEach(this.publications, publication => {
                languages.push({
                    language: publication.language
                })
            })

            languages = _.orderBy(languages, 'language')
            languages = _.uniqBy(languages, 'language')

            return languages
        },
        available_phases() {
            let phases = []

            _.forEach(this.publications, publication => {
                if (publication.phase) {
                    phases.push({
                        phase: publication.phase
                    })
                }
            })

            phases = _.orderBy(phases, 'phase')
            phases = _.uniqBy(phases, 'phase')

            return phases
        },
        active_filters() {
            let active_filters = false
            let f = this.filters
            if (f.states.length || f.grades.length || f.subjects.length || f.school_years.length || f.languages.length || f.phases.length || f.categories.length) {
                active_filters = true
            }
            return active_filters
        },
        can_create_publication() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'create_publication');
        },
        can_duplicate_publication() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'duplicate_publication');
        },
        can_import_publication() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'import_publication');
        },
        can_import_json() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'import_json_publication');
        },
        can_delete_publication() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'delete_publication');
        },
        can_lock_publication() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'lock_publication');
        }
    },
    watch: {
        filters: {
            handler(filters) {
                this.$localStorage.set('publicationFilters', filters)
            },
            deep: true,
            immediate: false
        },
        active_filters: {
            handler(active_filters) {
                if (active_filters) {
                    this.filters_visible = true
                }
            },
            immediate: true
        },
        sort_by: {
            handler(sort_by) {
                this.$localStorage.set('publicationSortBy', sort_by)
            }
        },
        sort_desc: {
            handler(sort_desc) {
                this.$localStorage.set('publicationSortDesc', sort_desc)
            }
        }
    },
    localStorage: {
        publicationFilters: {
            type: Object
        },
        publicationSortBy: {
            type: String
        },
        publicationSortDesc: {
            type: Boolean
        }
    },
    methods: {
        getStatusMessage(item) {
            if (item.publish3_status == 'failed') {
                this.$swal({
                    html: `<pre style="white-space: pre-wrap">${item.publish3_message}</pre>`,
                })
            }
        },
        getPublications() {
            this.loading_publications = true

            this.$store.dispatch('getPublications')
                .then(response => {
                    this.publications = _.orderBy(response.data, 'created')
                    this.addPortalSkuToPublication();

                    this.loading_publications = false
                })
        },
        createPublication() {
            this.publication = {}

            this.show_publication_modal = true
        },
        editPublication(publication) {
            this.publication = _.cloneDeep(publication)

            this.show_publication_modal = true
        },
        contentReport(publication) {
            this.publication = _.cloneDeep(publication)
            this.show_content_report_modal = true
        },
        importHistory(publication) {
            this.publication = _.cloneDeep(publication)
            this.show_import_history_modal = true
        },
        startPublicationDuplication(publication) {
            this.publication = _.cloneDeep(publication)

            this.show_duplicate_publication_modal = true
        },
        publicationDuplicated() {
            this.getPublications()

            this.show_duplicate_publication_modal = false
        },
        importTranslationJSON(publication) {
            this.publication = _.cloneDeep(publication)
            this.show_import_translation_json_modal = true
        },
        exportTranslationJSON(publication) {
            this.publication = _.cloneDeep(publication)
            this.show_export_translation_json_modal = true
        },
        publishPublication3FromPublicationList(pub) {
            let message = null

            if (pub.title) {
                message = 'Are you sure you want to publish the following publication to Studies Weekly 3.0? <div class="mt-3 pl-3 border-left text-left"><div>Title: <strong>' + pub.title + '</strong></div><div>SKU: <strong>' + pub.sku + '</strong></div></div>'
            } else {
                message = 'Are you sure you want to publish this publication to Studies Weekly 3.0?'
            }
            //need to validate that all panels have titles
            this.axios.get('/api/publications/validate_publication/' + pub.id)
                .then(response => {
                    if (response.data.valid) {// continue to publish
                        this.$swal({
                            // title: 'Publish Publication',
                            html: message,
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonText: 'Publish',
                            confirmButtonColor: '#27AE60',
                            cancelButtonText: 'Cancel',
                            reverseButtons: true
                        }).then((result) => {
                            if (result.value) {
                                this.axios.post('/api/publications/publish3/' + pub.id)
                            }
                        })
                    } else {//validation has failed
                        this.untitled_modals = response.data.untitled_panels || []
                        this.show_panel_validation_failed_modal = (
                            this.untitled_modals.length > 0 
                        )
                    }
                })
                .catch(error => {

                })
        },
        deletePublication(publication, index) {
            this.$swal({
                html: 'Are you sure you want to delete the following publication? <div class="mt-3 text-left pl-3 border-left"><div>Title: <strong>' + publication.title + '</strong></div></div>',
                showCancelButton: true,
                confirmButtonText: 'Delete Publication',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.axios.delete('/api/publications/delete/' + publication.id)
                        .then(() => {
                            this.publications.splice(index, 1)
                        })
                }
            })
        },
        async toggleLockPublication(publication) {
            const html = `
                Are you sure you want to ${publication.locked ? 'unlock' : 'lock'} the following publication?
                <div class="mt-3 text-left pl-3 border-left">
                    <div>Title: <strong> ${publication.title} </strong></div>
                </div>
            `
            const result = await this.$swal({
                html,
                showCancelButton: true,
                confirmButtonText: `${publication.locked ? 'Unlock' : 'Lock'} Publication`,
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })
            if (result.isConfirmed) {
                publication.locked = !publication.locked;
                try {
                    await this.axios.post(`/api/publications/update/${publication.id}`, {
                        locked: publication.locked,
                    })
                } catch(e) {
                    await this.$store.dispatch('pushError', {
                        data: {
                            message: `Failed to toggle publication lock: ${e.message}`,
                        },
                        time: moment().toISOString(),
                    })
                }
            }
        },
        publicationCreated(publication, response) {
            let publication_id = null

            if (
                response.data &&
                response.data.id
            ) {
                publication_id = response.data.id

                this.$swal({
                    title: 'Publication Created',
                    html: 'Would you like to go to the newly created publication "<strong>' + publication.title + '</strong>"?',
                    icon: 'success',
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                    confirmButtonColor: '#27AE60',
                    cancelButtonText: 'No',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.$router.push({
                            name: 'Publication',
                            params: {
                                publication_id: publication_id
                            }
                        })
                    }
                })
            }

            this.getPublications()
        },
        formattedPhase(phase) {
            if (phase) {
                return phase.replace(/_/g, " ")
            }
        },
        formattedCategory(category) {
            if (category) {
                const categoryObject = this.categories.find((item) => item.category === category)
                if (categoryObject) {
                    return categoryObject.name
                } else {
                    return ''
                }
            }
        },
        clearSort() {
            this.sort_by = 'sku'
            this.sort_desc = null

            this.$localStorage.remove('publicationSortBy')
            this.$localStorage.remove('publicationSortDesc')
        },
        addLanguage(new_language) {
            this.filters.languages.push(new_language)
        },
        removeLanguage(language) {
            let index = _.findIndex(this.filters.languages, {language: language.language})
            this.filters.languages.splice(index, 1)
        },
        clearFilters() {
            this.filters = {
                states: [],
                grades: [],
                school_years: [],
                languages: [],
                phases: [],
                subjects: [],
                categories: []
            }
        },
        toggleStateGrade(pub_id) {
            if (this.show_state_grades.includes(pub_id)) {
                var pub_id_index = this.show_state_grades.indexOf(pub_id);
                if (pub_id_index > -1) {
                    this.show_state_grades.splice(pub_id_index, 1);
                }
            } else {
                this.show_state_grades.push(pub_id);
            }

        },
        stateGradeIsHidden(pub_id) {
            if (this.show_state_grades.includes(pub_id)) {
                return true;
            } else {
                return false;
            }
        },
        addPortalSkuToPublication() {
            if(this.legacy_id_options){
                for (let index in this.publications) {
                    const option = this.legacy_id_options.find(option => option.ID == this.publications[index].legacy_id);
                    if (option) {
                        this.publications[index].portal_sku = option.sku
                    } else {
                        this.publications[index].portal_sku = ''
                    }
                }
            }
        }
    },
    mounted() {
        this.loading_publications = true
        this.axios.post("/api/publications/get_legacy_id_options")
            .then(response => {
                this.legacy_id_options = response.data.products;
                this.getPublications()

            })
            .catch(error => {
                this.checking_sample_sku = false
                if (error) {
                    console.log(error)
                }
            })


        if (this.$localStorage.get('publicationSortBy')) {
            if (this.$localStorage.get('publicationSortBy') == 'undefined') {
                this.$localStorage.remove('publicationSortBy')
                this.$localStorage.remove('publicationSortDesc')
            } else {
                this.sort_by = this.$localStorage.get('publicationSortBy')
                this.sort_desc = this.$localStorage.get('publicationSortDesc')
            }
        }

        if (window.location.host) {
            this.host = window.location.host
        }
    }
}
</script>
